import type { AutomationInputsType } from '@unifyapps/defs/types/dataSource';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import { memo, useMemo } from 'react';
import DataSourceHelper from '../../../helper/DataSourceHelper';
import type { UnsavedDataSource } from '../../../stores/DataSourceRecordStore';
import { useDSCBlocks, useDSCDependencyGraphProvider } from '../context/selectors';
import type { ApplicationDataSourceQueryNodeProps } from '../types';
import { getDefaultRetryConfig } from '../utils/retryConfig';
import { DataSourceWorkflowNode } from './DataSourceWorkflowNode';
import StreamingDataSourceWorkflowNode from './DataSourceWorkflowNode/StreamingDataSourceWorkflowNode';
import { InfiniteDataSourceWorkflowNode } from './InfiniteDataSourceWorkflowNode';
import { getDataSourcePaginationInfo } from './InfiniteDataSourceWorkflowNode/utils/getDataSourcePaginationInfo';

function ApplicationDataSourceQueryNode(props: ApplicationDataSourceQueryNodeProps) {
  const {
    id,
    dataSource,
    lookupRequests,
    onQueryCompletion,
    setDataSourceState,
    requestId,
    refetchOnPayloadChange,
    getCurrentQueryResult,
    disabled,
    originalDataSourceId,
    dependsOn,
    dynamicPaths,
    version,
  } = props;

  const blocks = useDSCBlocks();
  const { graphManager } = useDSCDependencyGraphProvider();

  const { isInfinite, infinitePaginationInfo } = useMemo(() => {
    return getDataSourcePaginationInfo({
      dataSource,
      blocks,
      dataSourceId: id,
      graphManager,
    });
  }, [blocks, dataSource, graphManager, id]);

  const isSSEStreamingEnabled = DataSourceHelper.isStreamingDataSource({
    properties: dataSource,
  } as UnsavedDataSource);

  const Component = useMemo(() => {
    if (isSSEStreamingEnabled) {
      return StreamingDataSourceWorkflowNode;
    }
    return isInfinite ? InfiniteDataSourceWorkflowNode : DataSourceWorkflowNode;
  }, [isSSEStreamingEnabled, isInfinite]);

  const defaultRetryConfig = useMemo(
    () =>
      getDefaultRetryConfig(
        DataSourceHelper.isAutomaticDataSource({
          id,
          entityType: UAEntityTypes.DataSource,
          properties: dataSource,
        }),
      ),
    [dataSource, id],
  );

  const transformerCode = useMemo(
    () => DataSourceHelper.getTransformerCodeFromOptions(dataSource.options),
    [dataSource.options],
  );

  const originalId = useMemo(
    () => dataSource.standardDataSourceId ?? originalDataSourceId,
    [dataSource.standardDataSourceId, originalDataSourceId],
  );

  return (
    <Component
      advancedOptions={dataSource.advancedOptions}
      callbacks={dataSource.callbacks}
      context={dataSource.context}
      defaultRetryConfig={defaultRetryConfig}
      dependsOn={dependsOn}
      disabled={disabled}
      dynamicPaths={dynamicPaths}
      getCurrentQueryResult={getCurrentQueryResult}
      id={id}
      infinitePaginationInfo={infinitePaginationInfo}
      inputs={dataSource.inputs as AutomationInputsType}
      lookupRequests={lookupRequests}
      name={dataSource.name}
      onQueryCompletion={onQueryCompletion}
      options={dataSource.options}
      originalDataSourceId={originalId}
      refetchOnPayloadChange={refetchOnPayloadChange}
      requestId={requestId}
      setDataSourceState={setDataSourceState}
      transformerCode={transformerCode}
      version={version}
    />
  );
}

export default memo(ApplicationDataSourceQueryNode);
